import { apiFetch } from '@/api/apiFetch';
import { throwResponseError } from '@/api/throwResponseError';
import { addDaysToDateString, todayInTimezone } from '@/lib/timezones';
import type {
  CreateTaskPayload,
  GetTasksResponse,
  TaskCompliance,
  GetTopPrioritiesResponse,
  Task,
  TaskPayload,
  UpdateTaskPayload,
} from './Tasks.types';

export const buildTaskUpdatePayload = (task: Task, patch: UpdateTaskPayload): TaskPayload => {
  const title = patch.title ?? task.title;
  const description = patch.description !== undefined ? patch.description : task.description;
  const status = patch.status ?? task.status;
  const schedule = patch.schedule !== undefined ? patch.schedule : task.schedule;
  const items = patch.items !== undefined ? { items: patch.items } : {};

  if (schedule) {
    return {
      title,
      description,
      status,
      dueDate: null,
      dueTime: null,
      schedule,
      ...items,
    };
  }

  const dueDate = patch.dueDate !== undefined ? patch.dueDate : task.dueDate;
  const dueTime = patch.dueTime !== undefined ? patch.dueTime : task.dueTime;

  return {
    title,
    description,
    status,
    dueDate,
    dueTime,
    schedule: null,
    ...items,
  };
};

export const getTomorrowFromTaskDueDate = (task: Task, timezone: string): string => {
  const today = todayInTimezone(timezone);
  const anchor = task.dueDate && task.dueDate > today ? task.dueDate : today;
  return addDaysToDateString(anchor, 1);
};

/** React Query key of the user's task list, shared by every screen that reads or updates it. */
export const TASKS_QUERY_KEY = ['tasks'] as const;

/** Under the tasks key, so anything that refreshes the tasks refreshes it too. */
export const TASK_COMPLIANCE_QUERY_KEY = [...TASKS_QUERY_KEY, 'compliance'] as const;

/** How the mandatory templates are going in the manager's stores. Managers and admins only. */
export const getTaskCompliance = async (): Promise<TaskCompliance[]> => {
  const res = await apiFetch('/task-templates/compliance', { method: 'GET' });
  if (!res.ok) {
    return throwResponseError(res);
  }
  const body = (await res.json()) as { templates: TaskCompliance[] };
  return body.templates ?? [];
};

export const getTasks = async (): Promise<Task[]> => {
  const res = await apiFetch(`/tasks`, {
    method: 'GET',
  });
  if (!res.ok) {
    return throwResponseError(res);
  }
  const data = (await res.json()) as GetTasksResponse;
  return data.tasks ?? [];
};

/** Fetches top priorities for today (backend-computed). */
export const getTopPriorities = async (): Promise<GetTopPrioritiesResponse> => {
  const res = await apiFetch(`/tasks/top-priorities`, {
    method: 'GET',
  });
  if (!res.ok) {
    return throwResponseError(res);
  }
  const raw = await res.json();
  return raw;
};

/** A personal task comes back as itself, store tasks as `{ tasks }`: one per store. */
export const createTask = async (payload: CreateTaskPayload): Promise<Task[]> => {
  const res = await apiFetch(`/tasks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    return throwResponseError(res);
  }
  const body = (await res.json()) as Task | GetTasksResponse;
  return 'tasks' in body ? body.tasks : [body];
};

export const updateTask = async (taskId: string, payload: TaskPayload): Promise<Task> => {
  const res = await apiFetch(`/tasks/${taskId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    return throwResponseError(res);
  }
  return (await res.json()) as Task;
};

export const setTaskItemChecked = async ({
  taskId,
  itemId,
  isChecked,
}: {
  taskId: string;
  itemId: string;
  isChecked: boolean;
}): Promise<Task> => {
  const res = await apiFetch(`/tasks/${taskId}/items/${itemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isChecked }),
  });
  if (!res.ok) {
    return throwResponseError(res);
  }
  return (await res.json()) as Task;
};

export const deleteTask = async (taskId: string): Promise<void> => {
  const res = await apiFetch(`/tasks/${taskId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    return throwResponseError(res);
  }
};
